import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Button,
    Description,
    EmptyState,
    EmptyStateIcon,
    List,
    type ListRenderItemInfo,
    ListSkeleton,
} from '@gitbook/spine-react';

import { useMagicBellAvailability } from './magicbell';
import { NotificationItem } from './NotificationItem';
import { type NotificationData, useNotifications } from './notifications';

type NotificationsPanelProps = {
    floating?: boolean;
    height?: number;
    width?: number;
};

/**
 * Panel listing notifications for the current user.
 */
export function NotificationsPanel(_props: NotificationsPanelProps): React.ReactElement {
    const allNotifications = useNotifications();

    const onPressMarkAllAsRead = () => {
        analytics.logEvent({
            type: 'notification_mark_all_as_read',
        });

        void allNotifications?.markAllAsRead();
    };

    return (
        <div className="flex h-full max-h-full w-auto flex-col md:size-[400px]">
            <div className="flex items-center justify-between gap-1 border-b-[0.5px] py-2 pr-3 pl-4">
                <div className="flex items-center gap-2">
                    <div className="flex-1 text-ui-heading-small">Notifications</div>
                    {typeof allNotifications?.unreadCount === 'number' &&
                        allNotifications.unreadCount > 0 && (
                            <Badge
                                kind="primary"
                                size="xsmall"
                                label={allNotifications.unreadCount}
                            />
                        )}
                </div>

                <div className="-mr-[5px] flex items-center gap-2">
                    <Button
                        iconOnly
                        leadingIcon={Icon.Check}
                        kind="blank"
                        onPress={onPressMarkAllAsRead}
                        label="Mark all as read"
                    />
                    <Button
                        iconOnly
                        leadingIcon={Icon.Settings}
                        kind="blank"
                        label="Notifications settings"
                        href={{
                            current: {
                                type: 'account',
                                tab: 'notification',
                            },
                        }}
                    />
                </div>
            </div>
            <NotificationsList />
        </div>
    );
}

/**
 * A list of all notifications.
 */
function NotificationsList() {
    const { status } = useMagicBellAvailability();
    const notifications = useNotifications();

    const renderItem = React.useCallback((itemProps: ListRenderItemInfo<NotificationData>) => {
        return <NotificationItem notification={itemProps.item} />;
    }, []);

    const onLoadNextPage = async () => {
        await notifications?.fetchNextPage();
    };

    if (status === 'loading' || (!notifications && status === 'ready')) {
        return <ListSkeleton className="px-2" />;
    }

    if (status !== 'ready') {
        return (
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.Bell />
                </EmptyStateIcon>
                <Description>Notifications are currently unavailable.</Description>
            </EmptyState>
        );
    }

    if (!notifications) {
        return (
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.Bell />
                </EmptyStateIcon>
                <Description>Notifications are currently unavailable.</Description>
            </EmptyState>
        );
    }

    if (!notifications.lastFetchedAt) {
        return <ListSkeleton className="px-2" />;
    }

    if (notifications.isEmpty) {
        return (
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.Bell />
                </EmptyStateIcon>
                <Description>Your GitBook updates will appear here!</Description>
            </EmptyState>
        );
    }

    return (
        <List
            compact
            items={notifications.notifications}
            renderItem={renderItem}
            footer={
                notifications.hasNextPage ? (
                    <Button
                        kind="secondary"
                        leadingIcon={Icon.ArrowDown}
                        onPress={onLoadNextPage}
                        label="Load more"
                        className="flex flex-col pt-4"
                    />
                ) : null
            }
        />
    );
}
